/**
 * Reporting metrics/columns that depend on invoice matching.
 * Hidden when the org does not have invoicing enabled.
 * Quote rate / quote opportunities are intentionally not gated here.
 */

import type { SessionContextType } from '../../../contexts/SessionContext';

/** Chart selector metric values (ranking + timeseries + overview widgets). */
export const INVOICING_GATED_CHART_METRICS = new Set([
    'totalCostVariance',
    'avgCostVariance',
    'medianCostVariance',
    'avgDeliveryVarianceDays',
    'medianDeliveryVarianceDays',
    'pctPartsInvoiced',
]);

/** Ranking table parent/legacy keys that expand to invoicing-dependent leaves. */
export const INVOICING_GATED_RANKING_PARENT_KEYS = new Set([
    'costVarianceToggle',
    'deliveryVarianceToggle',
    'pctPartsInvoiced',
]);

/** Ranking table leaf column keys. */
export const INVOICING_GATED_RANKING_LEAF_KEYS = new Set([
    'totalCostVariance',
    'avgCostVariance',
    'medianCostVariance',
    'avgDeliveryVarianceDays',
    'medianDeliveryVarianceDays',
    'pctPartsInvoiced',
]);

/** Auditing → Orders columns. */
export const INVOICING_GATED_AUDIT_ORDER_KEYS = new Set([
    'totalCostVariance',
    'partsInvoiced',
]);

/** Auditing → Parts columns. */
export const INVOICING_GATED_AUDIT_PART_KEYS = new Set([
    'isInvoiced',
    'costVariance',
    'invoicedCost',
    'invoiceDate',
    'finalEta',
]);

/**
 * True when invoice-dependent reporting metrics should be shown.
 * Until the session finishes loading, keep them visible so saved prefs aren't cleared.
 */
export function orgShowsInvoicingReportingMetrics(
    session: SessionContextType | undefined,
): boolean {
    return session?.isAuthenticated !== true || session.invoicingEnabled === true;
}

export function isInvoicingGatedChartMetric(metric: string): boolean {
    return INVOICING_GATED_CHART_METRICS.has(metric);
}

/** Overview widgets whose metric depends on invoice matching. */
export function isInvoicingGatedWidget(widget: { metric: string }): boolean {
    return INVOICING_GATED_CHART_METRICS.has(widget.metric);
}

export function filterChartMetricsForInvoicing<T extends { value: string }>(
    options: T[],
    invoicingEnabled: boolean,
): T[] {
    if (invoicingEnabled) return options;
    return options.filter((o) => !INVOICING_GATED_CHART_METRICS.has(o.value));
}

export function filterKeysForInvoicing(
    keys: string[],
    gated: Set<string>,
    invoicingEnabled: boolean,
): string[] {
    if (invoicingEnabled) return keys;
    return keys.filter((k) => !gated.has(k));
}
